import { useEffect, useState } from 'react';
import { PLANBOARD_TILE_CONFIG } from '@v2/pages/common/constants/base.constants';
import { UseSmartModalPositionProps, ModalPosition } from '@v2/pages/planboard/types';
import { fullWidth, fullHeight } from '@components/frame';
import { MODAL_ARROW } from '@v2/pages/planboard/constants';

// Hook to calculate modal position relative to an anchor element, ensuring it stays within viewport
export const useCalculateModalPosition = ({ isOpen, anchorRef, scale, modalWidth: modalWidthOverride }: UseSmartModalPositionProps): ModalPosition => {
  const [position, setPosition] = useState<ModalPosition>({ top: 0, left: 0, maxHeight: 0, isOpeningAbove: false, anchorTop: 0, arrowHorizontalPosition: 0 });

  useEffect(() => {
    if (!isOpen || !anchorRef.current) return;

    const updatePosition = () => {
      if (!anchorRef.current) return;

      const rect = anchorRef.current.getBoundingClientRect();
      const modalWidth = scale(modalWidthOverride ?? PLANBOARD_TILE_CONFIG.MODAL_WIDTH);
      const modalHeight = scale(PLANBOARD_TILE_CONFIG.MODAL_HEIGHT_ESTIMATE);
      const padding = scale(PLANBOARD_TILE_CONFIG.MODAL_EDGE_PADDING);

      const viewportWidth = fullWidth;
      const viewportHeight = fullHeight;

      // Vertical positioning: prefer below, flip to above if overflow
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const fitsBelow = spaceBelow >= modalHeight + padding;

      let top: number;
      let isOpeningAbove = false;

      if (fitsBelow) {
        top = rect.bottom + padding;
      } else if (spaceAbove >= modalHeight + padding) {
        // Opening above - position so modal bottom is near anchor top
        top = rect.top - modalHeight - padding;
        isOpeningAbove = true;
      } else {
        // Neither fits perfectly, position where there's more space
        if (spaceBelow > spaceAbove) {
          top = rect.bottom + padding;
        } else {
          top = Math.max(padding, rect.top - modalHeight - padding);
          isOpeningAbove = true;
        }
      }

      // Horizontal positioning: center on button, adjust for overflow
      const buttonCenterX = rect.left + rect.width / 2;
      let left = buttonCenterX - modalWidth / 2;

      // Adjust if overflowing right
      if (left + modalWidth > viewportWidth - padding) {
        left = viewportWidth - modalWidth - padding;
      }

      // Adjust if overflowing left
      if (left < padding) {
        left = padding;
      }

      // Calculate maxHeight based on opening direction
      let maxHeight: number;
      if (isOpeningAbove) {
        // Modal opens above: max height is space above minus padding
        maxHeight = rect.top - padding * 2;
      } else {
        // Modal opens below: constrain to viewport bottom
        maxHeight = viewportHeight - top - padding;
      }

      // anchorTop is the distance from viewport top to where modal bottom should align (anchor's top - padding)
      const anchorTop = rect.top - padding;

      // Calculate arrow horizontal position relative to modal
      const arrowHalfWidth = scale(MODAL_ARROW.HALF_WIDTH);
      let arrowHorizontalPosition = buttonCenterX - left;

      // Clamp arrow to stay within modal bounds (with padding)
      arrowHorizontalPosition = Math.max(arrowHalfWidth + 10, Math.min(arrowHorizontalPosition, modalWidth - arrowHalfWidth - 10));

      setPosition({ top, left, maxHeight, isOpeningAbove, anchorTop, arrowHorizontalPosition });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, anchorRef, scale, modalWidthOverride]);

  return position;
};
